import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { DollarSign, Plus, Filter, Wallet, TrendingUp, TrendingDown, Calendar, User, Mail, Edit, Trash2, UserCircle, Clock } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { Card, Button, DataTable, Spinner, Badge, Modal, FormField, FormSelect, FormDatePicker, FormReactSelect, Drawer, Divider } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';

const Payouts = () => {
  const { user: currentUser } = useAuth();
  const { showBrowserNotification, fetchNotifications } = useNotifications();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [payouts, setPayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [adminProfits, setAdminProfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [checkedPayouts, setCheckedPayouts] = useState(new Set());
  const [userDetails, setUserDetails] = useState({
    portfolio: null,
    transactions: [],
    allPayouts: [],
    loading: false
  });
  const [filters, setFilters] = useState({
    userId: '',
    role: '',
    startDate: '',
    endDate: ''
  });
  const [formData, setFormData] = useState({
    user_id: '',
    role: 'investor',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    admin_name: '',
    note: ''
  });
  const [maxAvailable, setMaxAvailable] = useState(0);
  const [amountError, setAmountError] = useState('');

  useEffect(() => {
    fetchPayouts();
    fetchUsers();
    fetchBrokers();
    fetchAdminProfits();
  }, [filters]);

  // Update maxAvailable when user, role, or payouts change
  useEffect(() => {
    if (formData.user_id && formData.role) {
      const available = getAvailableBalance(formData.role, formData.user_id);
      setMaxAvailable(Number((available || 0).toFixed(2)));
    } else {
      setMaxAvailable(0);
    }
  }, [formData.user_id, formData.role, users, brokers, adminProfits, payouts]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.userId) params.userId = filters.userId;
      if (filters.role) params.role = filters.role;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/api/payouts', { params });
      setPayouts(response.data.payouts);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      showError('Failed to fetch payouts');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await api.get('/api/brokers');
      setBrokers(response.data.brokers || []);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchAdminProfits = async () => {
    try {
      const response = await api.get('/api/admin-profit');
      setAdminProfits(response.data.adminProfits || []);
    } catch (error) {
      console.error('Error fetching admin profits:', error);
    }
  };

  const getAvailableBalance = (role, userId) => {
    if (!role || !userId) return 0;

    if (role === 'investor') {
      const user = users.find((u) => u._id === userId);
      if (!user) return 0;

      // Use balance_aed provided by the enriched /api/users endpoint
      // which follows (Total Days * Daily Rate) - Total Payouts
      const availableBalanceAED = user.balance_aed || 0;

      return Math.max(0, availableBalanceAED);
    }

    if (role === 'broker') {
      const user = users.find((u) => u._id === userId);
      if (!user) return 0;
      const broker = brokers.find((b) => b.email === user.email);
      if (!broker) return 0;

      // Broker commission is already in AED
      const currentCommission = broker.total_commission_aed ?? broker.totalCommissionAed ?? 0;

      return Math.max(0, currentCommission);
    }

    if (role === 'admin') {
      const totalAvailable = adminProfits.reduce((sum, ap) => sum + (ap.available_balance || 0), 0);
      return Math.max(0, totalAvailable);
    }

    return 0;
  };

  const handleOpenModal = async () => {
    // Refresh data to get latest balances before opening modal
    await Promise.all([
      fetchUsers(),
      fetchBrokers(),
      fetchAdminProfits()
    ]);

    setFormData({
      user_id: '',
      role: 'investor',
      amount: 0,
      date: new Date(),
      admin_name: currentUser?.name || `${currentUser?.firstName} ${currentUser?.lastName}`,
      note: ''
    });
    setMaxAvailable(0);
    setAmountError('');
    setShowModal(true);
  };

  const handleOpenEditModal = (payout) => {
    setEditingPayout(payout);
    // Parse date as local date to avoid timezone issues
    let payoutDate = null;
    if (payout.date) {
      const dateStr = payout.date;
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const year = parseInt(dateMatch[1], 10);
        const month = parseInt(dateMatch[2], 10) - 1;
        const day = parseInt(dateMatch[3], 10);
        payoutDate = new Date(year, month, day);
      } else {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          payoutDate = date;
        }
      }
    }

    setFormData({
      user_id: payout.user_id?._id || payout.user_id || '',
      role: payout.role || 'investor',
      amount: parseFloat((payout.amount || payout.amt || 0).toFixed(2)),
      date: payoutDate || new Date(),
      admin_name: payout.admin_name || '',
      note: payout.note || ''
    });
    const available = getAvailableBalance(payout.role || 'investor', payout.user_id?._id || payout.user_id || '');
    setMaxAvailable(Number((available || 0).toFixed(2)));
    setAmountError('');
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPayout) return;

    try {
      const available = getAvailableBalance(formData.role, formData.user_id);
      if (formData.amount > available) {
        showError(`Amount cannot exceed available balance of ${available.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED.`);
        return;
      }
      // Format date as YYYY-MM-DD to avoid timezone issues
      const updateData = {
        ...formData,
        date: formData.date instanceof Date
          ? (() => {
            const year = formData.date.getFullYear();
            const month = String(formData.date.getMonth() + 1).padStart(2, '0');
            const day = String(formData.date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })()
          : formData.date
      };

      await api.put(`/api/payouts/${editingPayout._id}`, updateData);
      showSuccess('Payout updated successfully');
      // Refresh notification list after update
      fetchNotifications();

      // Refresh all data to get updated balances
      await Promise.all([
        fetchPayouts(),
        fetchUsers(),
        fetchBrokers(),
        fetchAdminProfits()
      ]);

      setShowEditModal(false);
      setEditingPayout(null);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update payout');
    }
  };

  const handleDelete = async (payout) => {
    const confirmed = await confirm({
      title: 'Delete Payout',
      message: `Are you sure you want to delete this payout of ${((payout.amount || payout.amt) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} for ${getUserName(payout.user_id)}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/payouts/${payout._id}`);
      showSuccess('Payout deleted successfully');

      // Refresh all data to get updated balances (balance is restored on delete)
      await Promise.all([
        fetchPayouts(),
        fetchUsers(),
        fetchBrokers(),
        fetchAdminProfits()
      ]);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to delete payout');
    }
  };

  const handleBulkDelete = async () => {
    if (checkedPayouts.size === 0) {
      showError('Please select at least one payout to delete');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Payouts',
      message: `Are you sure you want to delete ${checkedPayouts.size} payout(${checkedPayouts.size !== 1 ? 's' : ''})? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete All',
    });

    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(checkedPayouts).map(payoutId =>
          api.delete(`/api/payouts/${payoutId}`)
        )
      );
      showSuccess(`Deleted ${checkedPayouts.size} payout(${checkedPayouts.size !== 1 ? 's' : ''})`);
      setCheckedPayouts(new Set());

      // Refresh all data to get updated balances
      await Promise.all([
        fetchPayouts(),
        fetchUsers(),
        fetchBrokers(),
        fetchAdminProfits()
      ]);
    } catch (error) {
      showError('Failed to delete some payouts');
    }
  };

  const togglePayoutCheck = (payoutId) => {
    const newChecked = new Set(checkedPayouts);
    if (newChecked.has(payoutId)) {
      newChecked.delete(payoutId);
    } else {
      newChecked.add(payoutId);
    }
    setCheckedPayouts(newChecked);
  };

  const toggleAllPayoutsCheck = (checked) => {
    if (checked) {
      setCheckedPayouts(new Set(payouts.map(p => p._id)));
    } else {
      setCheckedPayouts(new Set());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const available = getAvailableBalance(formData.role, formData.user_id);
      if (formData.amount > available) {
        showError(`Amount cannot exceed available balance of ${available.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED.`);
        return;
      }
      // Format date as YYYY-MM-DD to avoid timezone issues
      const submitData = {
        ...formData,
        date: formData.date instanceof Date
          ? (() => {
            const year = formData.date.getFullYear();
            const month = String(formData.date.getMonth() + 1).padStart(2, '0');
            const day = String(formData.date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })()
          : formData.date
      };

      const response = await api.post('/api/payouts', submitData);
      showSuccess('Payout created successfully');

      // Prefer using the created admin notification from the backend if available,
      // so the content exactly matches what will appear in the bell.
      const createdNotifications = response.data?.payout?._createdNotifications;
      const adminNotification = createdNotifications?.adminNotification;

      try {
        const amount = submitData.amount;
        const date = submitData.date;
        const roleLabel =
          submitData.role === 'admin'
            ? 'Admin'
            : submitData.role === 'broker'
              ? 'Broker'
              : 'Investor';

        // Browser notification
        showBrowserNotification(
          adminNotification?.title || 'Payout Created',
          {
            body:
              adminNotification?.message ||
              `Payout of ${amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} AED created for ${roleLabel}. Date: ${date}.`,
            tag: adminNotification?._id || response.data.payout?._id,
          }
        );
      } catch (err) {
        console.error('Error showing browser notification for payout:', err);
      }

      // Refresh header notification list so bell sees the new notification
      fetchNotifications();

      // Refresh all data to get updated balances
      await Promise.all([
        fetchPayouts(),
        fetchUsers(),
        fetchBrokers(),
        fetchAdminProfits()
      ]);

      setShowModal(false);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to create payout');
    }
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email || 'Unknown User';
  };

  const fetchUserDetails = async (userId) => {
    setUserDetails({ portfolio: null, transactions: [], allPayouts: [], loading: true });
    try {
      const [portfolioRes, transactionsRes, payoutsRes] = await Promise.all([
        api.get(`/api/users/${userId}/portfolio`).catch(() => ({ data: { portfolio: null } })),
        api.get(`/api/users/${userId}/transactions?limit=50`).catch(() => ({ data: { transactions: [] } })),
        api.get(`/api/payouts?userId=${userId}`).catch(() => ({ data: { payouts: [] } }))
      ]);

      setUserDetails({
        portfolio: portfolioRes.data.portfolio,
        transactions: transactionsRes.data.transactions || [],
        allPayouts: payoutsRes.data.payouts || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      setUserDetails({ portfolio: null, transactions: [], allPayouts: [], loading: false });
    }
  };

  const handlePayoutClick = async (payout) => {
    setSelectedPayout(payout);
    setShowDrawer(true);
    if (payout.user_id?._id) {
      await fetchUserDetails(payout.user_id._id);
    }
  };

  const tableColumns = [
    {
      key: 'checkbox',
      label: (
        <input
          type="checkbox"
          checked={payouts.length > 0 && checkedPayouts.size === payouts.length}
          onChange={(e) => toggleAllPayoutsCheck(e.target.checked)}
          className="rounded"
        />
      ),
      sortable: false,
      render: (payout) => (
        <input
          type="checkbox"
          checked={checkedPayouts.has(payout._id)}
          onChange={() => togglePayoutCheck(payout._id)}
          className="rounded"
        />
      ),
    },
    {
      key: 'user',
      label: 'User',
      render: (payout) => {
        const user = payout.user_id;
        const userId = user?._id || user || '';
        // Extract last 2 characters of MongoDB ObjectId or use a numeric representation
        const displayId = userId
          ? (typeof userId === 'string' ? userId.slice(-2) : String(userId).slice(-2))
          : 'N/A';
        return (
          <div className="flex items-center gap-3">
            <UserCircle
              size={24}
              className={
                payout.role === 'admin'
                  ? 'text-error-600 dark:text-error-400'
                  : payout.role === 'broker'
                    ? 'text-secondary-600 dark:text-secondary-400'
                    : 'text-primary-600 dark:text-primary-400'
              }
            />
            <div className="flex flex-col">
              <button
                onClick={() => handlePayoutClick(payout)}
                className="text-left font-medium text-primary-900 dark:text-primary-50 hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors cursor-pointer"
              >
                {getUserName(user)}
              </button>
              <span className="text-xs text-primary-600 dark:text-primary-400">
                ID: {displayId}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      render: (payout) => {
        const role = payout.role || 'investor';
        const badgeVariant = role === 'admin' ? 'error' : role === 'broker' ? 'info' : 'primary';
        const displayText = role === 'admin' ? 'Admin' : role === 'broker' ? 'Broker' : 'Investor';
        return (
          <Badge variant={badgeVariant} size="sm">
            {displayText}
          </Badge>
        );
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (payout) => (
        <span className="font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
          <DirhamSymbol size={16} inline />
          {((payout.amount || payout.amt) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'date',
      label: 'Date',
      render: (payout) => {
        const date = new Date(payout.date);
        return (
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-primary-600 dark:text-primary-400" />
            <span className="text-primary-900 dark:text-primary-50">
              {date.toISOString().split('T')[0]}
            </span>
          </div>
        );
      },
    },
    {
      key: 'admin',
      label: 'Admin',
      render: (payout) => (
        <span className="text-primary-900 dark:text-primary-50">
          {payout.admin_name || 'N/A'}
        </span>
      ),
    },
    {
      key: 'note',
      label: 'Note',
      render: (payout) => (
        <span className="text-primary-900 dark:text-primary-50">
          {payout.note || '-'}
        </span>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      render: (payout) => (
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-primary-600 dark:text-primary-400" />
          <span className="text-primary-900 dark:text-primary-50">
            {payout.createdAt
              ? `${new Date(payout.createdAt).toISOString().split('T')[0]} ${new Date(payout.createdAt).toLocaleTimeString()}`
              : 'N/A'}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (payout) => (
        <div className="flex items-center gap-2">
          {currentUser?.role === 'admin' && (
            <>
              <button
                onClick={() => handleOpenEditModal(payout)}
                className="p-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => handleDelete(payout)}
                className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <DollarSign className="text-primary-600 dark:text-primary-400" size={32} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Payouts
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
              Manage investor and admin payouts
            </p>
          </div>
        </div>
        {currentUser?.role === 'admin' && (
          <Button onClick={handleOpenModal} variant="primary" size="md" leftIcon={<Plus size={20} />}>
            Create Payout
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-2 mb-5">
          <Filter size={20} className="text-primary-600 dark:text-primary-400" />
          <h3 className="font-semibold text-primary-900 dark:text-primary-50">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormReactSelect
            label="User"
            name="userId"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            options={[
              { value: '', label: 'All Users' },
              ...users.map((u) => ({
                value: u._id,
                label: getUserName(u),
              })),
            ]}
            placeholder="All Users"
          />
          <FormReactSelect
            label="Role"
            name="role"
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            options={[
              { value: '', label: 'All Roles' },
              { value: 'investor', label: 'Investor' },
              { value: 'admin', label: 'Admin' },
            ]}
            placeholder="All Roles"
          />
          <FormDatePicker
            label="Start Date"
            name="startDate"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            placeholderText="Select start date"
          />
          <FormDatePicker
            label="End Date"
            name="endDate"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            placeholderText="Select end date"
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="p-6">
        {checkedPayouts.size > 0 && (
          <div className="mb-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg flex items-center justify-between">
            <span className="text-error-700 dark:text-error-400 font-medium">
              {checkedPayouts.size} payout(s) selected
            </span>
            <Button
              onClick={handleBulkDelete}
              variant="ghost"
              size="sm"
              className="text-error-600 dark:text-error-400 hover:bg-error-100 dark:hover:bg-error-900/30"
              leftIcon={<Trash2 size={18} />}
            >
              Delete Selected
            </Button>
          </div>
        )}
        <DataTable
          columns={tableColumns}
          data={payouts}
          emptyMessage="No payouts found"
          loading={loading}
          exportFileName="payouts"
          searchableFields={['investor_name', 'admin_name', 'status']}
        />
      </Card>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Create Payout"
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
              Type <span className="text-error-600">*</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'investor', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'investor'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Investor
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'admin', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'admin'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Admin
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'broker', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'broker'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Broker
              </button>
            </div>
          </div>
          <FormReactSelect
            label="User"
            name="user_id"
            required
            value={formData.user_id}
            onChange={(e) => {
              const userId = e.target.value;
              setFormData({ ...formData, user_id: userId });
              const available = getAvailableBalance(formData.role, userId);
              setMaxAvailable(Number((available || 0).toFixed(2)));
            }}
            options={
              formData.role === 'broker'
                ? brokers
                  .map((b) => {
                    // Find matching user record for this broker by email
                    const user = users.find(
                      (u) => u.email === b.email && u.role === 'broker'
                    );
                    if (!user) return null;
                    return {
                      value: user._id,
                      label: `${b.name} (${b.email})`,
                    };
                  })
                  .filter(Boolean)
                : users
                  .filter((u) => u.role === formData.role)
                  .map((u) => ({
                    value: u._id,
                    label: `${getUserName(u)} (${u.role})`,
                  }))
            }
            placeholder="Select user"
          />
          <FormField
            label="Amount (AED)"
            type="number"
            step="0.01"
            required
            value={formData.amount}
            onChange={(e) => {
              const raw = e.target.value;
              const value = parseFloat(raw);

              if (isNaN(value)) {
                setFormData({ ...formData, amount: 0 });
                setAmountError('');
                return;
              }

              if (maxAvailable > 0 && value > maxAvailable) {
                const clamped = Number(maxAvailable.toFixed(2));
                setFormData({ ...formData, amount: clamped });
                setAmountError(
                  `Amount cannot exceed available balance of ${maxAvailable.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} AED.`
                );
              } else if (value < 0) {
                setFormData({ ...formData, amount: 0 });
                setAmountError('Amount must be zero or positive.');
              } else {
                const rounded = Number(value.toFixed(2));
                setFormData({ ...formData, amount: rounded });
                setAmountError('');
              }
            }}
          />
          {amountError && (
            <p className="mt-1 text-xs text-error-600 dark:text-error-400">
              {amountError}
            </p>
          )}
          <p className="text-xs text-primary-600 dark:text-primary-400">
            Max payout (available balance):{' '}
            <span className="inline-flex items-center gap-1 font-semibold">
              <DirhamSymbol size={12} inline />
              {maxAvailable.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </p>
          <FormDatePicker
            label="Date"
            name="date"
            required
            value={formData.date}
            onChange={(e) => {
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1;
                  const day = parseInt(dateMatch[3], 10);
                  const dateValue = new Date(year, month, day);
                  setFormData({ ...formData, date: dateValue });
                } else {
                  setFormData({ ...formData, date: null });
                }
              } else {
                setFormData({ ...formData, date: null });
              }
            }}
            placeholderText="Select date"
          />
          <FormField
            label="Admin Name"
            type="text"
            required
            value={formData.admin_name}
            onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
          />
          <FormField
            label="Note"
            type="text"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            placeholder="Optional note"
          />
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              Create
            </Button>
            <Button
              type="button"
              onClick={() => setShowModal(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPayout(null);
          setFormData({
            user_id: '',
            role: 'investor',
            amount: 0,
            date: new Date().toISOString().split('T')[0],
            admin_name: '',
            note: ''
          });
        }}
        title="Edit Payout"
        size="md"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
              Type <span className="text-error-600">*</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'investor', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'investor'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Investor
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'admin', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'admin'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Admin
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'broker', user_id: '' })}
                className={`flex-1 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium ${formData.role === 'broker'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500 shadow-md'
                    : 'bg-white dark:bg-primary-800 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
              >
                Broker
              </button>
            </div>
          </div>
          <FormReactSelect
            label="User"
            name="user_id"
            required
            value={formData.user_id}
            onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
            options={users
              .filter((u) => u.role === formData.role)
              .map((u) => ({
                value: u._id,
                label: `${getUserName(u)} (${u.role})`,
              }))}
            placeholder="Select user"
            disabled
          />
          <FormField
            label="Amount (AED)"
            type="number"
            step="0.01"
            required
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
          />
          <FormDatePicker
            label="Date"
            name="date"
            required
            value={formData.date}
            onChange={(e) => {
              if (e.target.value) {
                const dateMatch = e.target.value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const year = parseInt(dateMatch[1], 10);
                  const month = parseInt(dateMatch[2], 10) - 1;
                  const day = parseInt(dateMatch[3], 10);
                  const dateValue = new Date(year, month, day);
                  setFormData({ ...formData, date: dateValue });
                } else {
                  setFormData({ ...formData, date: null });
                }
              } else {
                setFormData({ ...formData, date: null });
              }
            }}
            placeholderText="Select date"
          />
          <FormField
            label="Admin Name"
            type="text"
            required
            value={formData.admin_name}
            onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
          />
          <FormField
            label="Note"
            type="text"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            placeholder="Optional note"
          />
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              Update
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowEditModal(false);
                setEditingPayout(null);
                setFormData({
                  user_id: '',
                  role: 'investor',
                  amount: 0,
                  date: new Date().toISOString().split('T')[0],
                  admin_name: '',
                  note: ''
                });
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Payout Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedPayout(null);
          setUserDetails({ portfolio: null, transactions: [], allPayouts: [], loading: false });
        }}
        title={selectedPayout ? (
          <div>
            <div className="text-xl font-semibold text-primary-900 dark:text-primary-50">
              Payout Details
            </div>
            <div className="text-sm text-primary-600 dark:text-primary-400 mt-1">
              {getUserName(selectedPayout.user_id)}
            </div>
          </div>
        ) : 'Payout Details'}
        size="lg"
        position="right"
      >
        {selectedPayout && (
          <div className="space-y-6">
            {/* Payout Information */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Payout Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      Amount
                    </label>
                    <p className="text-2xl font-bold text-success-600 dark:text-success-400 mt-1 flex items-center gap-1">
                      <DirhamSymbol size={24} inline />
                      {((selectedPayout.amount || selectedPayout.amt) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      Date
                    </label>
                    <p className="text-lg font-semibold text-primary-900 dark:text-primary-50 mt-1">
                      {new Date(selectedPayout.date).toISOString().split('T')[0]}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      Role
                    </label>
                    <div className="mt-1">
                      <Badge variant={selectedPayout.role === 'admin' ? 'secondary' : 'primary'}>
                        {selectedPayout.role}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                      Processed By
                    </label>
                    <p className="text-base font-medium text-primary-900 dark:text-primary-50 mt-1">
                      {selectedPayout.admin_name || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* User Information */}
            {selectedPayout.user_id && (
              <div>
                <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                  User Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <User className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Name</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                        {getUserName(selectedPayout.user_id)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                    <Mail className="text-primary-400 dark:text-primary-600" size={20} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Email</p>
                      <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                        {selectedPayout.user_id.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {selectedPayout.user_id.invested_amount !== undefined && (
                    <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                      <Wallet className="text-primary-400 dark:text-primary-600" size={20} />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Invested Amount</p>
                        <p className="text-base font-semibold text-primary-900 dark:text-primary-50">
                          ${(selectedPayout.user_id.invested_amount || 0).toLocaleString()} USDT
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedPayout.user_id.balance_usdt !== undefined && (
                    <div className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
                      <DollarSign className="text-primary-400 dark:text-primary-600" size={20} />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-primary-600 dark:text-primary-400">Balance</p>
                        <p className="text-base font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                          <DirhamSymbol size={16} inline />
                          {(selectedPayout.user_id.balance_usdt || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Portfolio Summary */}
            {userDetails.portfolio && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Portfolio Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Value
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(userDetails.portfolio.totalValue || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                      <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                        Total Invested
                      </p>
                      <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                        ${(userDetails.portfolio.totalInvested || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                        Gain/Loss
                      </p>
                      <p className={`text-xl font-bold mt-1 ${(userDetails.portfolio.totalGainLoss || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                        }`}>
                        ${(userDetails.portfolio.totalGainLoss || 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                      <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                        Gain/Loss %
                      </p>
                      <p className={`text-xl font-bold mt-1 ${(userDetails.portfolio.totalGainLossPercent || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                        }`}>
                        {(userDetails.portfolio.totalGainLossPercent || 0).toFixed(2)}%
                      </p>
                    </Card>
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* Payout History Timeline */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Payout History ({userDetails.allPayouts.length})
              </h3>
              {userDetails.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="sm" />
                </div>
              ) : userDetails.allPayouts.length > 0 ? (
                <div className="space-y-4">
                  {userDetails.allPayouts.map((payout, index) => {
                    const isSelected = payout._id === selectedPayout._id;
                    return (
                      <div key={payout._id} className="relative pl-8 pb-4">
                        {/* Timeline line */}
                        {index < userDetails.allPayouts.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-primary-200 dark:bg-primary-700" />
                        )}
                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected
                            ? 'bg-success-500 dark:bg-success-600 border-success-600 dark:border-success-500'
                            : 'bg-success-100 dark:bg-success-900/30 border-success-400 dark:border-success-600'
                          }`}>
                          <DollarSign size={12} className="text-success-600 dark:text-success-400" />
                        </div>
                        {/* Content */}
                        <div className={`rounded-lg p-3 border ${isSelected
                            ? 'bg-success-50 dark:bg-success-900/30 border-success-300 dark:border-success-700'
                            : 'bg-primary-50 dark:bg-primary-900/50 border-primary-200 dark:border-primary-700'
                          }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="success" className="text-xs">
                                  Payout
                                </Badge>
                                {isSelected && (
                                  <Badge variant="primary" className="text-xs">
                                    Current
                                  </Badge>
                                )}
                                <span className="text-xs text-primary-500 dark:text-primary-400">
                                  {new Date(payout.date).toISOString().split('T')[0]}
                                </span>
                              </div>
                              <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                                Processed by: {payout.admin_name || 'N/A'}
                              </p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-lg font-bold text-success-600 dark:text-success-400 flex items-center gap-1">
                                +<DirhamSymbol size={18} inline />
                                {((payout.amount || payout.amt) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg">
                  <DollarSign size={32} className="mx-auto text-primary-400 dark:text-primary-600 mb-2" />
                  <p className="text-sm text-primary-600 dark:text-primary-400">
                    No payout history available
                  </p>
                </div>
              )}
            </div>

            {/* Transaction History */}
            {userDetails.transactions.length > 0 && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Recent Transactions ({userDetails.transactions.length})
                  </h3>
                  <div className="space-y-2">
                    {userDetails.transactions.slice(0, 5).map((tx) => (
                      <div
                        key={tx._id}
                        className="flex items-center justify-between p-2 bg-primary-50 dark:bg-primary-900/50 rounded border border-primary-200 dark:border-primary-700"
                      >
                        <div className="flex items-center gap-2">
                          {tx.transactionType === 'deposit' ? (
                            <TrendingUp size={16} className="text-success-600 dark:text-success-400" />
                          ) : (
                            <TrendingDown size={16} className="text-error-600 dark:text-error-400" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-primary-900 dark:text-primary-50 capitalize">
                              {tx.transactionType}
                            </p>
                            <p className="text-xs text-primary-500 dark:text-primary-400">
                              {new Date(tx.date || tx.createdAt).toISOString().split('T')[0]}
                            </p>
                          </div>
                        </div>
                        <p className={`text-sm font-semibold ${tx.transactionType === 'deposit'
                            ? 'text-success-600 dark:text-success-400'
                            : 'text-error-600 dark:text-error-400'
                          }`}>
                          <span className="flex items-center gap-1">
                            {tx.transactionType === 'deposit' ? '+' : '-'}
                            <DirhamSymbol size={14} inline />
                            {(tx.amount || 0).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    ))}
                    {userDetails.transactions.length > 5 && (
                      <p className="text-xs text-primary-500 dark:text-primary-400 text-center pt-2">
                        Showing 5 of {userDetails.transactions.length} transactions
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Payouts;
